/**
 * Hyperfixation Scroll — motor propriu, independent de revslider.js/gsap.
 * Tehnică: position: sticky (CSS) + getBoundingClientRect (progres manual)
 * + lerp în requestAnimationFrame. NU folosește ScrollTrigger/pin.
 * Poziții/mărimi: citite din atribute data-*, aplicate din JS cu matchMedia
 * (fără media query CSS legat de ID de secțiune).
 *
 * Traiectoria imaginilor: fiecare imagine pleacă din centru (lângă text),
 * se deplasează în linie dreaptă spre poziția ei "de bază" (unde ajunge la
 * scale 1), și continuă pe ACEEAȘI direcție până iese din cadru prin acea
 * latură. Nu există o poziție statică intermediară — mișcarea e continuă.
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 749px)';
  var LERP_FACTOR = 0.12;
  var SNAP_EPSILON = 0.0005;

  // Cât de "late" se suprapun ferestrele individuale ale imaginilor
  // (valoare mai mare = mai multe imagini vizibile simultan pe ecran).
  var WINDOW_OVERLAP_FACTOR = 2.4;

  // Fereastra locală (u = 0..1) în care imaginea apare/dispare prin fade,
  // ca să nu "sară" brusc la vizibilitate/invizibilitate.
  var FADE_IN_END = 0.12;
  var FADE_OUT_START = 0.8;

  function clamp01(v) {
    return Math.min(Math.max(v, 0), 1);
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function num(val, fallback) {
    var n = parseFloat(val);
    return isNaN(n) ? fallback : n;
  }

  function HyperfixInstance(root) {
    this.root = root;
    this.wrapper = root.querySelector('.hpx-wrapper');
    this.sticky = root.querySelector('.hpx-sticky');
    this.textEl = root.querySelector('.hpx-text, .hpx-text-image-frame');
    this.textImageFrame = root.querySelector('.hpx-text-image-frame');
    this.images = Array.prototype.slice.call(root.querySelectorAll('.hpx-image'));
    this.videoWrap = root.querySelector('.hpx-video-wrap');
    this.video = this.videoWrap ? this.videoWrap.querySelector('video') : null;

    this.rawProgress = 0;
    this.currentProgress = 0;
    this.ticking = false;
    this.videoStarted = false;

    this.stickyWidth = 0;
    this.stickyHeight = 0;

    if (!this.wrapper) return;

    this.phaseAEnd = num(this.wrapper.dataset.phaseA, 55) / 100;
    this.phaseBEnd = num(this.wrapper.dataset.phaseB, 80) / 100;
    this.textFadeStart = num(this.wrapper.dataset.textFadeStart, 75) / 100;
    this.textFadeEnd = num(this.wrapper.dataset.textFadeEnd, 85) / 100;

    this._onScroll = this.requestTick.bind(this);
    this._onResize = this.handleResize.bind(this);

    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize);

    this.layout();
    this.requestTick();
  }

  HyperfixInstance.prototype.handleResize = function () {
    this.layout();
    this.requestTick();
  };

  // Setează mărimile cadrelor (dependente de viewport) și cache-uiește
  // dimensiunile containerului sticky — fără CSS media query pe ID.
  HyperfixInstance.prototype.layout = function () {
    var mobile = isMobile();

    if (this.sticky) {
      this.stickyWidth = this.sticky.clientWidth;
      this.stickyHeight = this.sticky.clientHeight;
    }

    this.images.forEach(function (el) {
      var w = mobile ? el.dataset.widthMobile : el.dataset.widthDesktop;
      var h = mobile ? el.dataset.heightMobile : el.dataset.heightDesktop;
      el.style.width = num(w, 200) + 'px';
      el.style.height = num(h, 200) + 'px';
    });

    if (this.textImageFrame) {
      var tx = mobile ? this.textImageFrame.dataset.xMobile : this.textImageFrame.dataset.xDesktop;
      var ty = mobile ? this.textImageFrame.dataset.yMobile : this.textImageFrame.dataset.yDesktop;
      var tw = mobile ? this.textImageFrame.dataset.widthMobile : this.textImageFrame.dataset.widthDesktop;
      var th = mobile ? this.textImageFrame.dataset.heightMobile : this.textImageFrame.dataset.heightDesktop;

      this.textImageFrame.style.left = 'calc(50% + ' + num(tx, 0) + '%)';
      this.textImageFrame.style.top = 'calc(50% + ' + num(ty, 0) + '%)';
      this.textImageFrame.style.width = num(tw, 300) + 'px';
      this.textImageFrame.style.height = num(th, 150) + 'px';
    }

    if (this.videoWrap) {
      var vw = mobile ? this.videoWrap.dataset.widthMobile : this.videoWrap.dataset.widthDesktop;
      var vx = mobile ? this.videoWrap.dataset.xMobile : this.videoWrap.dataset.xDesktop;
      var vy = mobile ? this.videoWrap.dataset.yMobile : this.videoWrap.dataset.yDesktop;

      this.videoWrap.style.left = 'calc(50% + ' + num(vx, 0) + '%)';
      this.videoWrap.style.top = 'calc(50% + ' + num(vy, 0) + '%)';
      this.videoWrap.style.setProperty('--target-width', num(vw, 500) + 'px');
    }
  };

  HyperfixInstance.prototype.computeRaw = function () {
    var rect = this.wrapper.getBoundingClientRect();
    var total = this.wrapper.offsetHeight - window.innerHeight;
    if (total <= 0) {
      this.rawProgress = 0;
      return;
    }
    var scrolled = -rect.top;
    this.rawProgress = clamp01(scrolled / total);
  };

  HyperfixInstance.prototype.requestTick = function () {
    if (!this.ticking) {
      this.ticking = true;
      requestAnimationFrame(this.tick.bind(this));
    }
  };

  HyperfixInstance.prototype.tick = function () {
    this.computeRaw();
    this.currentProgress = lerp(this.currentProgress, this.rawProgress, LERP_FACTOR);

    if (Math.abs(this.currentProgress - this.rawProgress) > SNAP_EPSILON) {
      this.render();
      requestAnimationFrame(this.tick.bind(this));
    } else {
      this.currentProgress = this.rawProgress;
      this.render();
      this.ticking = false;
    }
  };

  HyperfixInstance.prototype.render = function () {
    var p = this.currentProgress;
    var mobile = isMobile();
    var phaseAEnd = this.phaseAEnd;
    var phaseBEnd = this.phaseBEnd;
    var total = this.images.length || 1;

    var slot = phaseAEnd / total;
    var windowSpan = Math.max(slot * WINDOW_OVERLAP_FACTOR, 0.03);

    var stickyW = this.stickyWidth || 1;
    var stickyH = this.stickyHeight || 1;

    // --- Traiectoria fiecărei imagini: centru -> poziția de bază -> ieșire din cadru ---
    this.images.forEach(function (el, index) {
      var delayFraction = num(el.dataset.delay, 0) / 100;
      var winStart = slot * index + delayFraction * slot;
      var winEnd = winStart + windowSpan;

      var u = clamp01((p - winStart) / (winEnd - winStart));

      var targetX = num(mobile ? el.dataset.xMobile : el.dataset.xDesktop, 0);
      var targetY = num(mobile ? el.dataset.yMobile : el.dataset.yDesktop, 0);

      // distanță traiectorie: cât de departe merge dincolo de poziția de bază
      var exitMultiplier = num(el.dataset.exitMultiplier, 300) / 100;
      var exitX = targetX * exitMultiplier;
      var exitY = targetY * exitMultiplier;

      // fracția din fereastra locală (u) la care imaginea atinge poziția de bază
      var arriveU = clamp01(1 / Math.max(exitMultiplier, 1.01));
      arriveU = Math.min(Math.max(arriveU, 0.08), 0.92);

      var startScale = num(el.dataset.startScale, 30) / 100;
      var exitScale = num(el.dataset.exitScale, 180) / 100;

      // poziție: interpolare liniară continuă, de la centru (0,0) la punctul de ieșire
      var posXPercent = lerp(0, exitX, u);
      var posYPercent = lerp(0, exitY, u);
      var posXPx = (posXPercent / 100) * stickyW;
      var posYPx = (posYPercent / 100) * stickyH;

      // scală: creștere din centru până la poziția de bază, apoi continuă să crească spre ieșire
      var scale;
      if (u < arriveU) {
        scale = lerp(startScale, 1, u / arriveU);
      } else {
        scale = lerp(1, exitScale, (u - arriveU) / (1 - arriveU));
      }

      // opacitate: apare rapid lângă text, rămâne vizibilă, dispare treptat la ieșirea din cadru
      var opacity;
      if (u < FADE_IN_END) {
        opacity = u / FADE_IN_END;
      } else if (u < FADE_OUT_START) {
        opacity = 1;
      } else {
        opacity = 1 - (u - FADE_OUT_START) / (1 - FADE_OUT_START);
      }

      el.style.transform =
        'translate(-50%, -50%) translate(' + posXPx.toFixed(1) + 'px, ' + posYPx.toFixed(1) + 'px) scale(' + scale.toFixed(3) + ')';
      el.style.opacity = clamp01(opacity);
    });

    // --- Video: crește în faza B (după ce imaginile și-au încheiat traseul), rămâne la mărime finală ---
    if (this.videoWrap) {
      var vStartScale = 0.4;
      var vScale, vOpacity;

      if (p < phaseAEnd) {
        vScale = vStartScale;
        vOpacity = 0;
      } else if (p < phaseBEnd) {
        var t3 = clamp01((p - phaseAEnd) / (phaseBEnd - phaseAEnd));
        vScale = lerp(vStartScale, 1, t3);
        vOpacity = t3;
      } else {
        vScale = 1;
        vOpacity = 1;
      }

      this.videoWrap.style.transform = 'translate(-50%, -50%) scale(' + vScale + ')';
      this.videoWrap.style.opacity = vOpacity;

      if (this.video) {
        if (p >= phaseAEnd && !this.videoStarted) {
          this.videoStarted = true;
          this.video.muted = true;
          var playPromise = this.video.play();
          if (playPromise && playPromise.catch) playPromise.catch(function () {});
        } else if (p < phaseAEnd - 0.02 && this.videoStarted) {
          this.videoStarted = false;
          this.video.pause();
        }
      }
    }

    // --- Textul principal dispare chiar înainte de finalul fazei B ---
    if (this.textEl) {
      var textFadeStart = this.textFadeStart;
      var textFadeEnd = this.textFadeEnd;
      var tOpacity;

      if (p < textFadeStart) {
        tOpacity = 1;
      } else if (p < textFadeEnd) {
        tOpacity = 1 - clamp01((p - textFadeStart) / (textFadeEnd - textFadeStart));
      } else {
        tOpacity = 0;
      }
      this.textEl.style.opacity = tOpacity;
    }
  };

  function pad2(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  /**
   * Carusel "Obsession Archives": la fiecare felie de scroll (archive_vh_per_item)
   * itemul activ se schimbă direct (fără interpolare/blend continuu), cu un
   * crossfade scurt CSS. Click pe thumbnail / săgeți sare direct la itemul respectiv.
   * Fiecare video are și un buton play/pause propriu, ca rezervă în caz că
   * autoplay-ul e blocat de browser.
   */
  function HyperfixArchive(root) {
    this.root = root;
    this.wrapper = root.querySelector('.hpx-archive-wrapper');
    this.sticky = root.querySelector('.hpx-archive-sticky');
    this.mediaContainer = root.querySelector('.hpx-archive-media');
    this.mediaItems = Array.prototype.slice.call(root.querySelectorAll('.hpx-archive-media-item'));
    this.thumbs = Array.prototype.slice.call(root.querySelectorAll('.hpx-archive-thumb'));
    this.prevBtn = root.querySelector('.hpx-archive-prev');
    this.nextBtn = root.querySelector('.hpx-archive-next');
    this.counterEl = root.querySelector('.hpx-archive-counter');
    this.coordsEl = root.querySelector('.hpx-archive-coords');
    this.titleEl = root.querySelector('.hpx-archive-item-title');
    this.sourceEl = root.querySelector('.hpx-archive-source');
    this.descriptionEl = root.querySelector('.hpx-archive-description');

    this.count = this.mediaItems.length;
    this.activeIndex = -1;
    this.ticking = false;

    // Modul se stabilește o singură dată, la încărcare (nu comută live la
    // redimensionare peste breakpoint).
    this.mobileMode = isMobile();

    if (!this.wrapper || this.count === 0) return;

    var self = this;

    if (this.mobileMode) {
      // --- Mobil: fără scroll-linking deloc — comutarea se face exclusiv din
      // click pe săgeți sau pe thumbnail (vezi goToIndex / handlerele de mai jos).
      this._onScroll = null;
      this._onResize = null;
    } else {
      // --- Desktop: neschimbat — scroll vertical al paginii determină itemul activ ---
      this._onScroll = this.requestTick.bind(this);
      this._onResize = this.requestTick.bind(this);

      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize);
      window.addEventListener('load', this._onResize);
    }

    this.thumbs.forEach(function (el, i) {
      el.addEventListener('click', function () {
        self.goToIndex(i);
      });
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        self.goToIndex(self.activeIndex - 1);
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        self.goToIndex(self.activeIndex + 1);
      });
    }

    // buton play/pause + click direct pe video, per item
    this.mediaItems.forEach(function (el) {
      var video = el.querySelector('video');
      if (!video) return;

      var toggleBtn = el.querySelector('.hpx-archive-play-toggle');

      function syncIcon() {
        if (toggleBtn) toggleBtn.textContent = video.paused ? '▶' : '❚❚';
      }

      function togglePlay(evt) {
        if (evt) evt.stopPropagation();
        if (video.paused) {
          video.muted = true;
          var playPromise = video.play();
          if (playPromise && playPromise.catch) playPromise.catch(function () {});
        } else {
          video.pause();
        }
      }

      video.addEventListener('play', syncIcon);
      video.addEventListener('pause', syncIcon);
      video.addEventListener('click', togglePlay);
      if (toggleBtn) toggleBtn.addEventListener('click', togglePlay);

      syncIcon();
    });

    if (this.mobileMode) {
      // fără scroll-linking: doar populăm starea inițială (textele, thumb activ)
      this.setActive(0);
    } else {
      this.requestTick();
    }
  }

  HyperfixArchive.prototype.requestTick = function () {
    if (!this.ticking) {
      this.ticking = true;
      requestAnimationFrame(this.tick.bind(this));
    }
  };

  HyperfixArchive.prototype.tick = function () {
    var rect = this.wrapper.getBoundingClientRect();
    var total = this.wrapper.offsetHeight - window.innerHeight;
    var progress = 0;

    if (total > 0) {
      progress = clamp01(-rect.top / total);
    }

    var index = Math.floor(progress * this.count);
    index = Math.min(Math.max(index, 0), this.count - 1);

    if (index !== this.activeIndex) {
      this.setActive(index);
    }

    this.ticking = false;
  };

  HyperfixArchive.prototype.goToIndex = function (index) {
    index = Math.min(Math.max(index, 0), this.count - 1);

    if (this.mobileMode) {
      // pe mobil comutarea e directă — nu există poziție de scroll de sincronizat
      this.setActive(index);
      return;
    }

    var total = this.wrapper.offsetHeight - window.innerHeight;
    if (total <= 0) {
      this.setActive(index);
      return;
    }

    var targetProgress = (index + 0.5) / this.count;
    var wrapperTop = this.wrapper.getBoundingClientRect().top + window.scrollY;
    var targetScroll = wrapperTop + targetProgress * total;

    window.scrollTo({ top: targetScroll, behavior: 'smooth' });
  };

  HyperfixArchive.prototype.setActive = function (index) {
    this.activeIndex = index;

    this.mediaItems.forEach(function (el, i) {
      var isActive = i === index;
      el.classList.toggle('is-active', isActive);

      var video = el.querySelector('video');
      if (!video) return;

      if (isActive) {
        video.muted = true;
        var playPromise = video.play();
        if (playPromise && playPromise.catch) playPromise.catch(function () {});
      } else {
        video.pause();
      }
    });

    this.thumbs.forEach(function (el, i) {
      el.classList.toggle('is-active', i === index);
    });

    if (this.prevBtn) this.prevBtn.disabled = index <= 0;
    if (this.nextBtn) this.nextBtn.disabled = index >= this.count - 1;

    var active = this.thumbs[index];
    if (!active) return;

    if (this.counterEl) {
      this.counterEl.textContent = '(' + pad2(index + 1) + '/' + pad2(this.count) + ')';
    }
    if (this.coordsEl) {
      this.coordsEl.textContent = active.dataset.coordinates || '';
    }
    if (this.titleEl) {
      this.titleEl.textContent = active.dataset.title || '';
    }
    if (this.sourceEl) {
      if (active.dataset.source) {
        this.sourceEl.href = active.dataset.source;
      } else {
        this.sourceEl.removeAttribute('href');
      }
    }
    if (this.descriptionEl) {
      this.descriptionEl.textContent = active.dataset.description || '';
    }
  };

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-hyperfix-section]');
    roots.forEach(function (root) {
      if (!root._hyperfixInstance) {
        root._hyperfixInstance = new HyperfixInstance(root);
      }
      if (!root._hyperfixArchive) {
        root._hyperfixArchive = new HyperfixArchive(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (evt) {
    initAll(evt.target);
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    var root = evt.target.querySelector('[data-hyperfix-section]');
    if (root && root._hyperfixInstance) {
      window.removeEventListener('scroll', root._hyperfixInstance._onScroll);
      window.removeEventListener('resize', root._hyperfixInstance._onResize);
      root._hyperfixInstance = null;
    }
    if (root && root._hyperfixArchive) {
      var archive = root._hyperfixArchive;
      if (!archive.mobileMode) {
        window.removeEventListener('scroll', archive._onScroll);
        window.removeEventListener('resize', archive._onResize);
        window.removeEventListener('load', archive._onResize);
      }
      root._hyperfixArchive = null;
    }
  });
})();